import { useState } from "react";
import { Link } from "react-router-dom";
import { SectionShell } from "@/pages/SectionShell";
import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";
import { TextField } from "@/components/Field";
import { Loaded } from "@/components/Loading";
import { EmptyState } from "@/components/EmptyState";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { useCallable } from "@/lib/hooks/useCallable";
import { call, CALLABLES } from "@/services/callables";
import { formatDateTime } from "@/lib/format";
import { isStale, STALE_DEVICE_DAYS } from "./rules";
import type { DeviceEnrolment, Kit } from "@/types";

/**
 * ## The devices the institution owns
 *
 * One row per piece of hardware, enrolled once. A device has no account, never
 * signs in and cannot enrol itself. The way in is **Add devices**: plug it into
 * this laptop and it is flashed, given its kit's network, asked who it is and
 * registered, from one button (K2). Registering by hand — typing what is
 * printed on a case — stays as the fallback for a device that cannot be
 * plugged in here.
 *
 * **A row names no person.** A device is handed to whoever is in the room, and
 * which seat it holds is decided by the teacher in the lesson. That is what
 * makes a set of thirty shareable, and it is why nothing on this page asks who
 * a device belongs to.
 */
export function DevicesPage() {
  const t = useT();
  const devices = useCallable<{ devices: DeviceEnrolment[] }>(CALLABLES.listDevices);
  const kits = useCallable<{ kits: Kit[] }>(CALLABLES.listKits);
  const [enrolling, setEnrolling] = useState(false);

  return (
    <SectionShell
      titleKey="devices.title"
      actions={
        <Link
          to="/devices/add"
          className="inline-flex items-center min-h-10 rounded-control bg-brand px-4 py-2
                     text-[14px] font-medium leading-5 text-white hover:bg-brand-hover"
        >
          {t("devices.add.link")}
        </Link>
      }
    >
      <p className="max-w-2xl text-ink-muted">{t("devices.intro")}</p>

      <div className="mt-6">
        <Button variant="secondary" onClick={() => setEnrolling(true)}>
          {t("devices.enrol")}
        </Button>
      </div>

      <div className="mt-8">
        <Loaded state={devices}>
          {(loaded) =>
            loaded.devices.length === 0 ? (
              <EmptyState titleKey="devices.none" bodyKey="devices.noneBody" />
            ) : (
              <DeviceTable
                devices={loaded.devices}
                kits={kits.data?.kits ?? []}
                onChanged={devices.reload}
              />
            )
          }
        </Loaded>
      </div>

      <EnrolDialog
        open={enrolling}
        onClose={() => setEnrolling(false)}
        onEnrolled={() => {
          setEnrolling(false);
          devices.reload();
        }}
      />
    </SectionShell>
  );
}

function DeviceTable({
  devices,
  kits,
  onChanged,
}: {
  devices: DeviceEnrolment[];
  kits: Kit[];
  onChanged: () => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const { success, showError } = useToast();
  const [busy, setBusy] = useState<string | undefined>(undefined);
  const kitName = (kitId: string | undefined) =>
    kitId ? (kits.find((kit) => kit.id === kitId)?.name ?? kitId) : undefined;

  /* Withdrawn devices sink to the bottom: the working set is what somebody
     standing at a cupboard needs to see. */
  const ordered = [...devices].sort((a, b) => {
    if (Boolean(a.revokedAt) !== Boolean(b.revokedAt)) return a.revokedAt ? 1 : -1;
    return a.deviceLabel.localeCompare(b.deviceLabel);
  });

  const revoke = async (device: DeviceEnrolment) => {
    setBusy(device.deviceId);
    try {
      await call(CALLABLES.revokeDevice, { deviceId: device.deviceId });
      success("common.saved");
      onChanged();
    } catch (error) {
      showError(error);
    } finally {
      setBusy(undefined);
    }
  };

  return (
    <table className="w-full border-collapse text-left text-[14px]">
      <thead className="border-b border-line bg-surface-muted text-ink-muted">
        <tr>
          <th scope="col" className="h-9 px-3 text-[13px] font-medium">{t("devices.column.label")}</th>
          <th scope="col" className="h-9 px-3 text-[13px] font-medium">{t("devices.column.id")}</th>
          <th scope="col" className="h-9 px-3 text-[13px] font-medium">{t("devices.column.kit")}</th>
          <th scope="col" className="h-9 px-3 text-[13px] font-medium">{t("devices.column.enrolled")}</th>
          <th scope="col" className="h-9 px-3 text-[13px] font-medium">{t("devices.column.state")}</th>
          <th scope="col" className="h-9 px-3 text-[13px] font-medium">
            <span className="sr-only">{t("devices.column.actions")}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {ordered.map((device) => (
          <tr key={device.deviceId} className="border-b border-line last:border-0">
            <td className="h-10 px-3 py-2.5 text-ink">{device.deviceLabel}</td>
            <td className="h-10 px-3 py-2.5 font-mono text-xs text-ink-muted">{device.deviceId}</td>
            <td className="h-10 px-3 py-2.5 text-ink-muted">
              {kitName(device.kitId) ?? t("devices.noKit")}
            </td>
            <td className="h-10 px-3 py-2.5 text-ink-muted">
              {formatDateTime(locale, device.enrolledAt)}
            </td>
            <td className="h-10 px-3 py-2.5">
              {device.revokedAt ? (
                <span className="text-danger">
                  {t("devices.withdrawnOn", {
                    date: formatDateTime(locale, device.revokedAt),
                  })}
                </span>
              ) : isStale(device, Date.now()) ? (
                /*
                  Flagged, never withdrawn automatically. A studio running the
                  same course twice a year would otherwise lose its whole set
                  between terms, and registering thirty devices again is
                  somebody's afternoon. The choice stays a person's.
                */
                <span className="text-ink-muted">
                  {t("devices.notSeen", { days: STALE_DEVICE_DAYS })}
                </span>
              ) : (
                <span className="text-ink">{t("devices.inService")}</span>
              )}
            </td>
            <td className="h-10 px-3 py-2.5 text-right">
              {device.revokedAt ? null : (
                <Button
                  variant="secondary"
                  busy={busy === device.deviceId}
                  onClick={() => void revoke(device)}
                >
                  {t("devices.withdraw")}
                </Button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Registering one by hand.
 *
 * The fallback for a device that cannot be plugged into this laptop: the id
 * and the key are read off the case, and the label is whatever the school
 * writes on it. Nothing is generated here — the key already exists, on the
 * device.
 */
function EnrolDialog({
  open,
  onClose,
  onEnrolled,
}: {
  open: boolean;
  onClose: () => void;
  onEnrolled: () => void;
}) {
  const t = useT();
  const { success, showError } = useToast();
  const [deviceId, setDeviceId] = useState("");
  const [publicKey, setPublicKey] = useState("");
  const [deviceLabel, setDeviceLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await call(CALLABLES.enrolDevice, {
        deviceId: deviceId.trim(),
        publicKey: publicKey.trim(),
        deviceLabel: deviceLabel.trim(),
      });
      success("common.saved");
      setDeviceId("");
      setPublicKey("");
      setDeviceLabel("");
      onEnrolled();
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} titleKey="devices.enrol">
      <p className="text-sm text-ink-muted">{t("devices.enrolHelp")}</p>

      <div className="mt-4 flex flex-col gap-4">
        <TextField
          labelKey="devices.field.label"
          value={deviceLabel}
          onChange={(event) => setDeviceLabel(event.target.value)}
          hintKey="devices.field.labelHint"
        />
        <TextField
          labelKey="devices.field.id"
          value={deviceId}
          onChange={(event) => setDeviceId(event.target.value)}
        />
        <TextField
          labelKey="devices.field.key"
          value={publicKey}
          onChange={(event) => setPublicKey(event.target.value)}
          hintKey="devices.field.keyHint"
        />
      </div>

      <div className="mt-6 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <Button
          busy={busy}
          disabled={!deviceId.trim() || !publicKey.trim() || !deviceLabel.trim()}
          onClick={() => void submit()}
        >
          {t("devices.enrol")}
        </Button>
      </div>
    </Modal>
  );
}
